// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// The determination dossier — one panel, opened from the map, the timeline, the
// holders graph and the register, so a determination reads identically wherever
// the reader met it.
//
// THE VERBATIM RULE. The description of who holds native title (NTHOLD), the
// full case name and the corporation's name are reproduced exactly as the
// register states them: `.verbatim` sets `white-space: pre-wrap` and there is
// no clamp, no ellipsis, no title-casing and no max-height at any breakpoint.
// Abbreviating the description of who holds native title over country to make a
// panel tidy is not a layout decision, and this is the one place on the site
// where the full text always appears.

import { openOverlay, closeButton } from '../overlay';
import { esc, longDate, km2, plural } from '../format';
import { FINDING, METHOD, TENURE, TENURE_ORDER, type Tenure } from '../theme';
import { glossTerm } from './glossary';
import { setParams } from '../router';
import type { Determination, OutcomePolygonRow, Holder } from '../types';

let outcomeIndex: Map<string, OutcomePolygonRow[]> | null = null;
let holderIndex: Map<string, Holder[]> | null = null;

/** Views that have loaded the supporting registers hand them over once. */
export function indexOutcomes(rows: OutcomePolygonRow[]): void {
  const m = new Map<string, OutcomePolygonRow[]>();
  for (const r of rows) {
    if (!m.has(r.id)) m.set(r.id, []);
    m.get(r.id)!.push(r);
  }
  outcomeIndex = m;
}

export function indexHolders(rows: Holder[]): void {
  const m = new Map<string, Holder[]>();
  for (const h of rows) {
    for (const id of h.determinations) {
      if (!m.has(id)) m.set(id, []);
      m.get(id)!.push(h);
    }
  }
  holderIndex = m;
}

function field(label: string, value: string, opts: { verbatim?: boolean; mono?: boolean } = {}): string {
  if (!value) return '';
  const cls = [opts.verbatim ? 'verbatim' : '', opts.mono ? 'mono' : ''].filter(Boolean).join(' ');
  return `<div class="detail-field">
    <div class="detail-label">${esc(label)}</div>
    <div class="${cls}">${opts.verbatim ? esc(value) : value}</div>
  </div>`;
}

function tenureBreakdown(id: string): string {
  const rows = outcomeIndex?.get(id);
  if (!rows?.length) return '';
  const byTenure = new Map<Tenure, number>();
  for (const r of rows) {
    if (!r.tenure) continue;
    byTenure.set(r.tenure, (byTenure.get(r.tenure) ?? 0) + r.areaSqKm);
  }
  if (!byTenure.size) return '';
  const total = [...byTenure.values()].reduce((a, b) => a + b, 0);
  const bars = TENURE_ORDER.filter((t) => byTenure.has(t)).map((t) => {
    const v = byTenure.get(t)!;
    const share = total > 0 ? (v / total) * 100 : 0;
    const style = TENURE[t];
    return `<div class="tb-row">
      <div class="tb-head">
        <span class="legend-swatch" style="background:${style.fill}"></span>
        <span class="grow">${esc(style.label)}</span>
        <span class="mono xsmall shrink0">${esc(km2(v))}</span>
      </div>
      <div class="tb-track"><div class="tb-fill" style="width:${share.toFixed(2)}%;background:${style.fill}"></div></div>
    </div>`;
  }).join('');
  return `<div class="detail-block">
    <h3>What the court found, area by area</h3>
    <p class="xsmall muted">These are the register's own per-polygon areas within this determination — a
      determination area is usually a mosaic, not one category. ${esc(plural(rows.length, 'outcome polygon'))}.</p>
    <div class="tenure-bars">${bars}</div>
  </div>`;
}

export function openDetermination(d: Determination): void {
  openOverlay({
    panelClass: 'drawer',
    label: `Determination: ${d.name ?? d.id}`,
    lockScroll: true,
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      h.className = 'verbatim';
      h.textContent = d.name ?? d.id;
      head.append(h, closeButton(close, 'Close this determination'));

      const finding = d.finding ? FINDING[d.finding] : null;
      const method = d.method ? METHOD[d.method] : null;
      const holders = holderIndex?.get(d.id) ?? [];

      const body = document.createElement('div');
      body.className = 'stack';
      body.innerHTML = `
        <div class="detail-headline">
          <span class="pill" style="border-color:${finding?.fill ?? 'var(--border-default)'}">
            <span class="legend-swatch" style="background:${finding?.fill ?? 'transparent'}"></span>
            ${esc(finding?.label ?? d.findingText ?? 'Outcome not recorded')}
          </span>
          ${method ? `<span class="pill">${esc(method.label)}</span>` : ''}
          ${d.sea ? '<span class="pill">Sea determination</span>' : ''}
          ${d.appealed ? '<span class="pill">Appealed</span>' : ''}
        </div>
        <p class="small muted">${esc(finding?.plain ?? '')}</p>

        ${d.holders ? `<div class="detail-block">
          <h3>Native title holders${glossIconSafe('native-title')}</h3>
          <p class="xsmall muted">Reproduced exactly as the National Native Title Register states it.</p>
          <div class="verbatim holders-text">${esc(d.holders)}</div>
        </div>` : ''}

        ${tenureBreakdown(d.id)}

        <div class="detail-block">
          <h3>The matter</h3>
          <div class="detail-grid">
            ${field('Determination date', esc(longDate(d.date)))}
            ${field('Entered on the register', esc(longDate(d.registered)))}
            ${field('How it was decided', method ? `${esc(method.label)} <span class="xsmall muted">— ${esc(method.plain)}</span>` : '—')}
            ${field('Court', esc(d.body ?? '—'))}
            ${field('Judge', esc(d.judge ?? 'Not recorded'))}
            ${field('Federal Court file', esc(d.caseNo ?? '—'), { mono: true })}
            ${field('Jurisdiction', esc(d.juris))}
            ${field('Determination area', `${esc(km2(d.areaSqKm, { exact: true }))} <span class="xsmall muted">— the area the court considered, not the area recognised</span>`)}
            ${field('Tribunal reference', esc(d.id), { mono: true })}
            ${field('Status', esc(d.type ?? '—'))}
          </div>
          ${d.caseName ? `<div class="detail-field"><div class="detail-label">Case name</div><div class="verbatim small">${esc(d.caseName)}</div></div>` : ''}
        </div>

        ${holders.length ? `<div class="detail-block">
          <h3>Held by${glossIconSafe('rntbc')}</h3>
          <ul class="plain-list">${holders.map((x) => `<li>
            <span class="verbatim">${esc(x.name)}</span>
            ${x.register ? `<a class="small" href="${esc(x.register)}" target="_blank" rel="noopener">Corporate register ↗</a>` : ''}
          </li>`).join('')}</ul>
        </div>` : (d.bodyCorporate ? `<div class="detail-block">
          <h3>Held by${glossIconSafe('rntbc')}</h3>
          <div class="verbatim">${esc(d.bodyCorporate)}</div>
        </div>` : '')}

        <div class="detail-actions">
          ${d.judgment ? `<a class="btn" href="${esc(d.judgment)}" target="_blank" rel="noopener">Read the Federal Court judgment ↗</a>` : ''}
        </div>

        <div class="note legal xsmall">The boundaries shown on this site are indicative only. They are not
          cadastral or legal boundaries, they cannot be used to establish rights over any particular parcel
          of land or water, and nothing here is legal advice. The judgment linked above, and the Tribunal’s
          own registers, are the authoritative record.</div>
      `;

      panel.append(head, body);
    },
    onClose() {
      // Clear the deep link, or a reload reopens a panel the reader closed.
      setParams({ id: null });
    },
  });
  setParams({ id: d.id });
}

/** The glossary icon markup, guarded so a missing key cannot inject a stray button. */
function glossIconSafe(key: string): string {
  const html = glossTerm(key, '');
  return html.includes('data-term') ? ` <button type="button" class="glossary-icon" data-term="${esc(key)}" aria-label="What does this term mean?">i</button>` : '';
}
